import { AnimatePresence, motion } from 'framer-motion';
import { FC } from 'react';

import {
  Caption,
  Icon,
  LoadingIndicator,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
  useSidebar,
} from '@eluve/components';
import { useSyncableFileCount } from '@eluve/user-local-files';
import { useIsOnline } from '@eluve/utility-hooks';

const fadeAnimation = {
  animate: { opacity: 1 },
  initial: { opacity: 0 },
  exit: { opacity: 0 },
  transition: { duration: 0.2 },
} as const;

interface CoreGlobalStatusWidgetProps {
  isCollapsed: boolean;
  isOnline: boolean;
  uploadCount: number;
}

export const CoreGlobalStatusWidget: FC<CoreGlobalStatusWidgetProps> = ({
  isCollapsed,
  isOnline,
  uploadCount,
}) => {
  const shouldShow = !isOnline || uploadCount > 0;
  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div {...fadeAnimation}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Stack
                align="stretch"
                justify="center"
                className={cn(
                  'h-10 overflow-hidden rounded-md border border-sidebar-border bg-sidebar-accent p-2 text-sidebar-foreground transition-[width,height,padding] select-none',
                  isCollapsed && 'size-10! p-3!',
                )}
                gap={2}
              >
                <AnimatePresence mode="popLayout">
                  <motion.div
                    key={isOnline ? 'uploading' : 'offline'}
                    {...fadeAnimation}
                  >
                    <Stack
                      align="center"
                      justify={isCollapsed ? 'center' : 'start'}
                      direction="horizontal"
                    >
                      <Text
                        inline
                        color="inherit"
                        className={cn(
                          'flex-1 overflow-hidden transition-[opacity,width]',
                          isCollapsed && 'w-0 opacity-0',
                        )}
                        weight="regular"
                      >
                        {isOnline ? 'Uploading' : 'Offline'}
                      </Text>
                      {isOnline ? (
                        <Stack
                          className="relative size-5"
                          align="center"
                          justify="center"
                          direction="horizontal"
                        >
                          <LoadingIndicator className="size-5.5 stroke-[1.5px]" />
                          <Caption
                            inline
                            className={cn(
                              'absolute transition-opacity',
                              uploadCount > 1 ? 'opacity-100' : 'opacity-0',
                            )}
                            color="inherit"
                            size="3xs"
                          >
                            {uploadCount > 9 ? <>&#xFF0B;</> : uploadCount}
                          </Caption>
                        </Stack>
                      ) : (
                        <Icon
                          className="animate-[pulse_4s_ease-in-out_infinite]"
                          name="CircleDashed"
                        />
                      )}
                    </Stack>
                  </motion.div>
                </AnimatePresence>
              </Stack>
            </TooltipTrigger>
            {isOnline ? (
              <TooltipContent
                className="w-54"
                side={isCollapsed ? 'right' : 'top'}
              >
                Uploading {uploadCount > 1 ? uploadCount : 'a'} file
                {uploadCount > 1 ? 's' : ''}
                <span className="inline-block opacity-50">
                  Please keep Eluve open and your device connected to the
                  internet until this is done.
                </span>
              </TooltipContent>
            ) : (
              <TooltipContent
                className="w-54"
                side={isCollapsed ? 'right' : 'top'}
              >
                You are currently offline
                <span className="inline-block opacity-50">
                  If you are in session you can continue recording, but some
                  features like note generation might require a connection.
                </span>
              </TooltipContent>
            )}
          </Tooltip>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

interface GlobalStatusWidgetProps {
  isRecording: boolean;
}

export const GlobalStatusWidget: FC<GlobalStatusWidgetProps> = ({
  isRecording,
}) => {
  const { isMobile, state } = useSidebar();
  const isCollapsed = state === 'collapsed' && !isMobile;
  const isOnline = useIsOnline();
  const uploadCount = useSyncableFileCount();

  return (
    <CoreGlobalStatusWidget
      isCollapsed={isCollapsed}
      isOnline={isOnline}
      uploadCount={isRecording ? 0 : uploadCount}
    />
  );
};
